/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */

import { useEffect, useRef, useState } from 'react';

import useForage from 'contexts/ForageContext/useForage';
import { Alert } from 'honeydew/Alert';
import { LoadingIcon } from 'honeydew/Loader';
import logError from 'utils/errorUtils';

import { createCardElement, destroyCardElement } from './utils';

/**
 * ForageCardElement - A React component that integrates with Forage's EBT card tokenization service
 *
 * @param {Object} props - Component props
 * @param {string} [props.elementID='ebt-card-element-container'] - Unique ID for the DOM container where the card element will be mounted
 * @param {Function} [props.onReady] - Callback function called when the card element is ready. Receives the element instance as parameter
 * @param {Function} [props.onChange] - Callback function called when card validation state changes. Receives (event, isValid)
 */
function ForageCardElement({ elementID = 'ebt-card-element-container', onChange, onReady }) {
    const { forage, isLoading, isReady } = useForage();
    const cardElementRef = useRef(null);
    const isInitializing = useRef(false);
    const [cardElementError, setCardElementError] = useState(null);

    // Initialize the Forage card element when dependencies are ready
    useEffect(() => {
        if (!forage || !isReady || isInitializing.current) return;

        const initializeCardElement = async () => {
            isInitializing.current = true;
            setCardElementError(null);

            try {
                // Clean up any existing element before creating a new one
                if (cardElementRef.current) {
                    destroyCardElement(cardElementRef.current);
                }

                const element = await createCardElement({
                    forage,
                    elementID,
                    onReady,
                    onChange: (event) => {
                        const isValid = event.complete && !event.error;
                        onChange?.(event, isValid);
                    },
                });

                cardElementRef.current = element;
            } catch (initError) {
                setCardElementError(initError);
            } finally {
                isInitializing.current = false;
            }
        };

        initializeCardElement();
    }, [forage, isReady, elementID]);

    // Cleanup: destroy card element on component unmount
    useEffect(
        () => () => {
            if (cardElementRef.current) {
                destroyCardElement(cardElementRef.current);
            }
        },
        []
    );

    // Log errors for debugging
    useEffect(() => {
        if (cardElementError) {
            logError(`EBT Card Element Error: ${cardElementError?.message || 'Unknown error'}`, { elementID });
        }
    }, [cardElementError]);

    // Show loading state while Forage is initializing
    if (isLoading) {
        return (
            <p className="flex h-full items-center justify-center">
                <LoadingIcon />
            </p>
        );
    }

    // Show error state if card element creation failed
    if (cardElementError) {
        return (
            <Alert variant="error">
                Failed to create card input. Please{' '}
                <button type="button" className="underline" onClick={() => window.location.reload()}>
                    refresh the page
                </button>{' '}
                &amp; try again.
            </Alert>
        );
    }

    // Render the card element container
    return (
        <div className="relative">
            <label className="text-body-xs absolute -top-10 left-10 rounded-md bg-white px-5 font-grotesk-bold">
                EBT Card Number *
            </label>
            {/* Using aria-label instead of htmlFor since divs cannot be labeled according to HTML spec */}
            <div id={elementID} aria-label="EBT Card Number" style={{ height: '70px' }}>
                {/* Card element will be mounted here */}
            </div>
        </div>
    );
}

export default ForageCardElement;
